import { useEffect, useState, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbList,
  BreadcrumbPage,
} from '@/components/ui/breadcrumb'
import { Separator } from '@/components/ui/separator'
import { SidebarTrigger } from '@/components/ui/sidebar'
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Button } from '@/components/ui/button'
import { Skeleton } from '@/components/ui/skeleton'
import { Badge } from '@/components/ui/badge'
import { Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '@/components/ui/tabs'
import { CreateSellSheet } from '@/components/sheet/CreateSellSheet'
import { fetchProducts, fetchProductQuantities } from '@/services/products'
import type { Product, ProductQuantity } from '@/types/products'
import {
  AlertCircle,
  Package,
  Scale,
  Search,
  X,
  ArrowUpDown,
  Hash,
  Layers,
  ShoppingCart,
  ArrowUp,
  ArrowDown,
} from 'lucide-react'

type SortField = 'material' | 'color' | 'type' | 'square' | 'height' | 'width' | 'quantity' | 'weight' | 'must_weight' | 'price' | 'full_weight' | 'created_at'
type SortDirection = 'asc' | 'desc' | null

function LoadingSkeleton() {
  return (
    <div className='space-y-2'>
      {[...Array(5)].map((_, i) => (
        <Skeleton key={i} className='h-16 w-full' />
      ))}
    </div>
  )
}

export default function Products() {
  const { t, i18n } = useTranslation()
  const [activeTab, setActiveTab] = useState('inventory')

  // Products state
  const [products, setProducts] = useState<Product[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  // Quantities state
  const [quantities, setQuantities] = useState<ProductQuantity[]>([])
  const [isLoadingQuantities, setIsLoadingQuantities] = useState(false)
  const [quantitiesError, setQuantitiesError] = useState<string | null>(null)
  const [hasLoadedQuantities, setHasLoadedQuantities] = useState(false)

  // Filter state
  const [searchQuery, setSearchQuery] = useState('')
  const [filterMaterial, setFilterMaterial] = useState<string>('all')

  // Quantities filter state
  const [quantitySearchQuery, setQuantitySearchQuery] = useState('')
  const [quantityFilterThickness, setQuantityFilterThickness] = useState<string>('all')

  // Sort state
  const [sortField, setSortField] = useState<SortField>('created_at')
  const [sortDirection, setSortDirection] = useState<SortDirection>('desc')

  // Sell sheet state
  const [isSellSheetOpen, setIsSellSheetOpen] = useState(false)
  const [selectedProduct, setSelectedProduct] = useState<ProductQuantity | null>(null)

  useEffect(() => {
    const loadProducts = async () => {
      try {
        setIsLoading(true)
        setError(null)
        const data = await fetchProducts()
        setProducts(data)
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to load products')
      } finally {
        setIsLoading(false)
      }
    }

    loadProducts()
  }, [])

  useEffect(() => {
    if (activeTab === 'inventory' && !hasLoadedQuantities && !isLoadingQuantities) {
      const loadQuantities = async () => {
        try {
          setIsLoadingQuantities(true)
          setQuantitiesError(null)
          const data = await fetchProductQuantities()
          const sortedData = [...data].sort((a, b) => {
            if (a.cell !== b.cell) {
              return b.cell - a.cell
            }
            return a.thickness - b.thickness
          })
          setQuantities(sortedData)
        } catch (err) {
          setQuantitiesError(err instanceof Error ? err.message : 'Failed to load inventory')
        } finally {
          setIsLoadingQuantities(false)
          setHasLoadedQuantities(true)
        }
      }

      loadQuantities()
    }
  }, [activeTab, hasLoadedQuantities, isLoadingQuantities])

  // Get unique materials for filter
  const uniqueMaterials = useMemo(() => {
    const materials = new Set<string>()
    products.forEach((p) => materials.add(p.material.name))
    return Array.from(materials).sort()
  }, [products])

  // Get unique thicknesses for quantities filter
  const uniqueThicknesses = useMemo(() => {
    const thicknesses = new Set<number>()
    quantities.forEach((q) => thicknesses.add(q.thickness))
    return Array.from(thicknesses).sort((a, b) => a - b)
  }, [quantities])

  const handleSort = (field: SortField) => {
    if (sortField === field) {
      if (sortDirection === 'asc') {
        setSortDirection('desc')
      } else if (sortDirection === 'desc') {
        setSortField('created_at')
        setSortDirection('desc')
      } else {
        setSortDirection('asc')
      }
    } else {
      setSortField(field)
      setSortDirection('asc')
    }
  }

  const getSortIcon = (field: SortField) => {
    if (sortField !== field) {
      return <ArrowUpDown className="ml-1 h-3 w-3" />
    }
    if (sortDirection === 'asc') {
      return <ArrowUp className="ml-1 h-3 w-3" />
    }
    if (sortDirection === 'desc') {
      return <ArrowDown className="ml-1 h-3 w-3" />
    }
    return <ArrowUpDown className="ml-1 h-3 w-3" />
  }

  const filteredProducts = useMemo(() => {
    let result = products.filter((item) => {
      const searchLower = searchQuery.toLowerCase()
      const matchesSearch =
        searchQuery === '' ||
        item.material.name.toLowerCase().includes(searchLower) ||
        item.square.toString().includes(searchLower) ||
        item.height.toString().includes(searchLower) ||
        item.width.toString().includes(searchLower)

      const matchesMaterial =
        filterMaterial === 'all' || item.material.name === filterMaterial

      return matchesSearch && matchesMaterial
    })

    if (sortField && sortDirection) {
      result = [...result].sort((a, b) => {
        let aValue: string | number
        let bValue: string | number

        switch (sortField) {
          case 'material':
            aValue = a.material.name
            bValue = b.material.name
            break
          case 'color':
            aValue = a.color.name
            bValue = b.color.name
            break
          case 'type':
            aValue = a.type.name
            bValue = b.type.name
            break
          case 'square':
            aValue = a.square
            bValue = b.square
            break
          case 'height':
            aValue = a.height
            bValue = b.height
            break
          case 'width':
            aValue = a.width
            bValue = b.width
            break
          case 'quantity':
            aValue = a.quantity
            bValue = b.quantity
            break
          case 'weight':
            aValue = a.weight
            bValue = b.weight
            break
          case 'must_weight':
            aValue = a.must_weight
            bValue = b.must_weight
            break
          case 'price':
            aValue = a.price
            bValue = b.price
            break
          case 'full_weight':
            aValue = a.full_weight
            bValue = b.full_weight
            break
          case 'created_at':
            aValue = new Date(a.created_at).getTime()
            bValue = new Date(b.created_at).getTime()
            break
          default:
            return 0
        }

        if (typeof aValue === 'string' && typeof bValue === 'string') {
          const comparison = aValue.localeCompare(bValue)
          return sortDirection === 'asc' ? comparison : -comparison
        }

        if (aValue < bValue) return sortDirection === 'asc' ? -1 : 1
        if (aValue > bValue) return sortDirection === 'asc' ? 1 : -1

        return 0
      })
    }

    return result
  }, [products, searchQuery, filterMaterial, sortField, sortDirection])

  const clearFilters = () => {
    setSearchQuery('')
    setFilterMaterial('all')
  }

  const hasActiveFilters = searchQuery !== '' || filterMaterial !== 'all'

  const filteredQuantities = useMemo(() => {
    return quantities.filter((item) => {
      const searchLower = quantitySearchQuery.toLowerCase()
      const matchesSearch =
        quantitySearchQuery === '' ||
        item.thickness.toString().includes(searchLower) ||
        item.cell.toString().includes(searchLower) ||
        item.height.toString().includes(searchLower) ||
        item.width.toString().includes(searchLower)

      const matchesThickness =
        quantityFilterThickness === 'all' || item.thickness.toString() === quantityFilterThickness

      return matchesSearch && matchesThickness
    })
  }, [quantities, quantitySearchQuery, quantityFilterThickness])

  const clearQuantityFilters = () => {
    setQuantitySearchQuery('')
    setQuantityFilterThickness('all')
  }

  const hasActiveQuantityFilters = quantitySearchQuery !== '' || quantityFilterThickness !== 'all'

  // Sell handlers
  const handleOpenSell = (product: ProductQuantity) => {
    setSelectedProduct(product)
    setIsSellSheetOpen(true)
  }

  const handleSellSuccess = async () => {
    const data = await fetchProductQuantities()
    setQuantities(data)
  }

  const locale = i18n.language === 'ru' ? 'ru-RU' : 'uz-UZ'

  const formatWeight = (weight: number) => {
    return new Intl.NumberFormat(locale, {
      maximumFractionDigits: 2,
    }).format(weight) + ' kg'
  }

  const formatNumber = (num: number) => {
    return new Intl.NumberFormat(locale, {
      maximumFractionDigits: 2,
    }).format(num)
  }

  const totalProducts = products.length
  const totalCount = products.reduce((sum, p) => sum + p.quantity, 0)
  const totalWeight = products.reduce((sum, p) => sum + p.weight, 0)

  // Quantity totals
  const totalQuantityTypes = quantities.length
  const totalQuantityCount = quantities.reduce((sum, q) => sum + q.total_count, 0)
  const totalQuantityWeight = quantities.reduce((sum, q) => sum + q.total_weight, 0)

  return (
    <>
      <header className='flex h-16 shrink-0 items-center gap-2'>
        <div className='flex items-center gap-2 px-4'>
          <SidebarTrigger className='-ml-1' />
          <Separator
            orientation='vertical'
            className='mr-2 data-[orientation=vertical]:h-4'
          />
          <Breadcrumb>
            <BreadcrumbList>
              <BreadcrumbItem>
                <BreadcrumbPage>{t('products.title')}</BreadcrumbPage>
              </BreadcrumbItem>
            </BreadcrumbList>
          </Breadcrumb>
        </div>
      </header>

      {(error || quantitiesError) && (
        <div className='mx-4 mb-4 flex items-center gap-2 rounded-lg border border-red-200 bg-red-50 p-4 text-red-700 dark:border-red-800 dark:bg-red-950 dark:text-red-400'>
          <AlertCircle className='h-4 w-4' />
          <span>{error || quantitiesError}</span>
        </div>
      )}

      <div className='flex flex-1 flex-col gap-4 p-4 pt-0'>
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList>
            <TabsTrigger value='inventory'>
              <Layers className='mr-2 h-4 w-4' />
              {t('products.inventory')}
            </TabsTrigger>
            <TabsTrigger value='products'>
              <Package className='mr-2 h-4 w-4' />
              {t('products.title')}
            </TabsTrigger>
          </TabsList>

          {/* Products Tab */}
          <TabsContent value='products' className='space-y-4'>
            {/* Summary Cards */}
            <div className='grid gap-4 md:grid-cols-3'>
              <Card>
                <CardHeader className='flex flex-row items-center justify-between pb-2'>
                  <CardTitle className='text-sm font-medium'>
                    {t('products.productTypes')}
                  </CardTitle>
                  <Package className='h-4 w-4 text-muted-foreground' />
                </CardHeader>
                <CardContent>
                  <div className='text-2xl font-bold'>{totalProducts}</div>
                </CardContent>
              </Card>
              <Card>
                <CardHeader className='flex flex-row items-center justify-between pb-2'>
                  <CardTitle className='text-sm font-medium'>
                    {t('products.totalCount')}
                  </CardTitle>
                  <Hash className='h-4 w-4 text-muted-foreground' />
                </CardHeader>
                <CardContent>
                  <div className='text-2xl font-bold'>{formatNumber(totalCount)}</div>
                </CardContent>
              </Card>
              <Card>
                <CardHeader className='flex flex-row items-center justify-between pb-2'>
                  <CardTitle className='text-sm font-medium'>
                    {t('products.totalWeight')}
                  </CardTitle>
                  <Scale className='h-4 w-4 text-muted-foreground' />
                </CardHeader>
                <CardContent>
                  <div className='text-2xl font-bold'>{formatWeight(totalWeight)}</div>
                </CardContent>
              </Card>
            </div>

            {/* Main Table Card */}
            <Card>
          <CardHeader>
            <CardTitle>{t('products.productQuantities')}</CardTitle>
            <CardDescription>
              {t('products.viewInventory')}
            </CardDescription>
          </CardHeader>
          <CardContent>
            {/* Filters */}
            <div className='mb-4 flex flex-wrap items-center gap-3'>
              <div className='relative flex-1 min-w-50'>
                <Search className='absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground' />
                <Input
                  placeholder={t('products.searchPlaceholder')}
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className='pl-9'
                />
              </div>
              <Select value={filterMaterial} onValueChange={setFilterMaterial}>
                <SelectTrigger className='w-40'>
                  <SelectValue placeholder={t('products.material')} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value='all'>{t('products.allMaterials')}</SelectItem>
                  {uniqueMaterials.map((material) => (
                    <SelectItem key={material} value={material}>
                      {material}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {hasActiveFilters && (
                <Button variant='ghost' size='sm' onClick={clearFilters}>
                  <X className='mr-1 h-4 w-4' />
                  {t('common.clear')}
                </Button>
              )}
            </div>

            {isLoading ? (
              <LoadingSkeleton />
            ) : filteredProducts.length === 0 ? (
              <div className='py-8 text-center text-muted-foreground'>
                {products.length === 0
                  ? t('products.noProductsFound')
                  : t('products.noProductsMatch')}
              </div>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead
                      className='cursor-pointer select-none hover:bg-muted/50'
                      onClick={() => handleSort('material')}
                    >
                      <div className='flex items-center'>
                        {t('products.material')}
                        {getSortIcon('material')}
                      </div>
                    </TableHead>
                    <TableHead
                      className='cursor-pointer select-none hover:bg-muted/50'
                      onClick={() => handleSort('color')}
                    >
                      <div className='flex items-center'>
                        {t('materials.color')}
                        {getSortIcon('color')}
                      </div>
                    </TableHead>
                    <TableHead
                      className='cursor-pointer select-none hover:bg-muted/50'
                      onClick={() => handleSort('type')}
                    >
                      <div className='flex items-center'>
                        {t('materials.type')}
                        {getSortIcon('type')}
                      </div>
                    </TableHead>
                    <TableHead
                      className='cursor-pointer select-none hover:bg-muted/50 text-right'
                      onClick={() => handleSort('square')}
                    >
                      <div className='flex items-center justify-end'>
                        {t('products.square')}
                        {getSortIcon('square')}
                      </div>
                    </TableHead>
                    <TableHead
                      className='cursor-pointer select-none hover:bg-muted/50 text-right'
                      onClick={() => handleSort('height')}
                    >
                      <div className='flex items-center justify-end'>
                        {t('products.height')}
                        {getSortIcon('height')}
                      </div>
                    </TableHead>
                    <TableHead
                      className='cursor-pointer select-none hover:bg-muted/50 text-right'
                      onClick={() => handleSort('width')}
                    >
                      <div className='flex items-center justify-end'>
                        {t('products.width')}
                        {getSortIcon('width')}
                      </div>
                    </TableHead>
                    <TableHead
                      className='cursor-pointer select-none hover:bg-muted/50 text-right'
                      onClick={() => handleSort('quantity')}
                    >
                      <div className='flex items-center justify-end'>
                        {t('products.count')}
                        {getSortIcon('quantity')}
                      </div>
                    </TableHead>
                    <TableHead
                      className='cursor-pointer select-none hover:bg-muted/50 text-right'
                      onClick={() => handleSort('weight')}
                    >
                      <div className='flex items-center justify-end'>
                        {t('products.weight')}
                        {getSortIcon('weight')}
                      </div>
                    </TableHead>
                    <TableHead
                      className='cursor-pointer select-none hover:bg-muted/50 text-right'
                      onClick={() => handleSort('must_weight')}
                    >
                      <div className='flex items-center justify-end'>
                        {t('products.mustWeight')}
                        {getSortIcon('must_weight')}
                      </div>
                    </TableHead>
                    <TableHead
                      className='cursor-pointer select-none hover:bg-muted/50 text-right'
                      onClick={() => handleSort('price')}
                    >
                      <div className='flex items-center justify-end'>
                        {t('common.price')}
                        {getSortIcon('price')}
                      </div>
                    </TableHead>
                    <TableHead className='text-center'>
                      {t('common.deleted')}
                    </TableHead>
                    <TableHead
                      className='cursor-pointer select-none hover:bg-muted/50 text-right'
                      onClick={() => handleSort('full_weight')}
                    >
                      <div className='flex items-center justify-end'>
                        {t('products.fullWeight')}
                        {getSortIcon('full_weight')}
                      </div>
                    </TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredProducts.map((item) => (
                    <TableRow key={item.id}>
                      <TableCell>
                        <span className='rounded-full bg-primary/10 text-primary px-2 py-1 text-xs font-medium'>
                          {item.material.name}
                        </span>
                      </TableCell>
                      <TableCell>
                        {item.color.name}
                      </TableCell>
                      <TableCell>
                        {item.type.name}
                      </TableCell>
                      <TableCell className='text-right'>
                        {formatNumber(item.square)}
                      </TableCell>
                      <TableCell className='text-right'>
                        {formatNumber(item.height)}
                      </TableCell>
                      <TableCell className='text-right'>
                        {formatNumber(item.width)}
                      </TableCell>
                      <TableCell className='text-right font-medium'>
                        {formatNumber(item.quantity)}
                      </TableCell>
                      <TableCell className='text-right font-medium'>
                        {formatWeight(item.weight)}
                      </TableCell>
                      <TableCell className='text-right font-medium'>
                        {formatWeight(item.must_weight)}
                      </TableCell>
                      <TableCell className='text-right font-medium'>
                        {formatNumber(item.price)}
                      </TableCell>
                      <TableCell className='text-center'>
                        {item.is_deleted ? (
                          <Badge variant='destructive' className='text-white'>
                            {t('common.yes')}
                          </Badge>
                        ) : (
                          <Badge variant='secondary'>
                            {t('common.no')}
                          </Badge>
                        )}
                      </TableCell>
                      <TableCell className='text-right font-medium'>
                        {formatWeight(item.full_weight)}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}

            {/* Results count */}
            {!isLoading && products.length > 0 && (
              <div className='mt-4 text-sm text-muted-foreground'>
                {t('common.showing')} {filteredProducts.length} {t('common.of')} {products.length}
              </div>
            )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Inventory Tab */}
          <TabsContent value='inventory' className='space-y-4'>
            {/* Summary Cards */}
            <div className='grid gap-4 md:grid-cols-3'>
              <Card>
                <CardHeader className='flex flex-row items-center justify-between pb-2'>
                  <CardTitle className='text-sm font-medium'>
                    {t('products.productTypes')}
                  </CardTitle>
                  <Layers className='h-4 w-4 text-muted-foreground' />
                </CardHeader>
                <CardContent>
                  <div className='text-2xl font-bold'>{totalQuantityTypes}</div>
                </CardContent>
              </Card>
              <Card>
                <CardHeader className='flex flex-row items-center justify-between pb-2'>
                  <CardTitle className='text-sm font-medium'>
                    {t('products.totalCount')}
                  </CardTitle>
                  <Hash className='h-4 w-4 text-muted-foreground' />
                </CardHeader>
                <CardContent>
                  <div className='text-2xl font-bold'>{formatNumber(totalQuantityCount)}</div>
                </CardContent>
              </Card>
              <Card>
                <CardHeader className='flex flex-row items-center justify-between pb-2'>
                  <CardTitle className='text-sm font-medium'>
                    {t('products.totalWeight')}
                  </CardTitle>
                  <Scale className='h-4 w-4 text-muted-foreground' />
                </CardHeader>
                <CardContent>
                  <div className='text-2xl font-bold'>{formatWeight(totalQuantityWeight)}</div>
                </CardContent>
              </Card>
            </div>

            {/* Inventory Table Card */}
            <Card>
              <CardHeader>
                <CardTitle>{t('products.inventory')}</CardTitle>
                <CardDescription>
                  {t('products.inventoryDescription')}
                </CardDescription>
              </CardHeader>
              <CardContent>
                {/* Filters */}
                <div className='mb-4 flex flex-wrap items-center gap-3'>
                  <div className='relative flex-1 min-w-50'>
                    <Search className='absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground' />
                    <Input
                      placeholder={t('products.searchPlaceholder')}
                      value={quantitySearchQuery}
                      onChange={(e) => setQuantitySearchQuery(e.target.value)}
                      className='pl-9'
                    />
                  </div>
                  <Select value={quantityFilterThickness} onValueChange={setQuantityFilterThickness}>
                    <SelectTrigger className='w-40'>
                      <SelectValue placeholder={t('products.thickness')} />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value='all'>{t('products.allThicknesses')}</SelectItem>
                      {uniqueThicknesses.map((thickness) => (
                        <SelectItem key={thickness} value={thickness.toString()}>
                          {formatNumber(thickness)}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  {hasActiveQuantityFilters && (
                    <Button variant='ghost' size='sm' onClick={clearQuantityFilters}>
                      <X className='mr-1 h-4 w-4' />
                      {t('common.clear')}
                    </Button>
                  )}
                </div>

                {isLoadingQuantities ? (
                  <LoadingSkeleton />
                ) : filteredQuantities.length === 0 ? (
                  <div className='py-8 text-center text-muted-foreground'>
                    {quantities.length === 0
                      ? t('products.noProductsFound')
                      : t('products.noProductsMatch')}
                  </div>
                ) : (
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead className='text-right'>
                          {t('products.cell')}
                        </TableHead>
                        <TableHead className='text-right'>
                          {t('products.thickness')}
                        </TableHead>
                        <TableHead className='text-right'>
                          {t('products.height')}
                        </TableHead>
                        <TableHead className='text-right'>
                          {t('products.width')}
                        </TableHead>
                        <TableHead className='text-right'>
                          {t('products.color')}
                        </TableHead>
                        <TableHead className='text-right'>
                          {t('products.type')}
                        </TableHead>
                        <TableHead className='text-right'>
                          {t('products.count')}
                        </TableHead>
                        <TableHead className='text-right'>
                          {t('products.weight')}
                        </TableHead>
                        <TableHead className='w-16'>
                          {t('common.actions')}
                        </TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {filteredQuantities.map((item, index) => (
                        <TableRow key={`${item.thickness}-${item.cell}-${item.height}-${item.width}-${index}`}>
                          <TableCell className='text-right'>
                            {formatNumber(item.cell)}
                          </TableCell>
                          <TableCell className='text-right'>
                            {formatNumber(item.thickness)}
                          </TableCell>
                          <TableCell className='text-right'>
                            {formatNumber(item.height)}
                          </TableCell>
                          <TableCell className='text-right'>
                            {formatNumber(item.width)}
                          </TableCell>
                          <TableCell className='text-right'>
                            {item.color__name}
                          </TableCell>
                          <TableCell className='text-right'>
                            {item.type__name}
                          </TableCell>
                          <TableCell className='text-right font-medium'>
                            {formatNumber(item.total_count)}
                          </TableCell>
                          <TableCell className='text-right font-medium'>
                            {formatWeight(item.total_weight)}
                          </TableCell>
                          <TableCell>
                            <Button
                              variant='ghost'
                              size='icon'
                              onClick={() => handleOpenSell(item)}
                              title={t('sell.createSale')}
                            >
                              <ShoppingCart className='h-4 w-4' />
                            </Button>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                )}

                {/* Results count */}
                {!isLoadingQuantities && quantities.length > 0 && (
                  <div className='mt-4 text-sm text-muted-foreground'>
                    {t('common.showing')} {filteredQuantities.length} {t('common.of')} {quantities.length}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>

      {/* Sell Sheet */}
      <CreateSellSheet
        open={isSellSheetOpen}
        onOpenChange={(open) => {
          setIsSellSheetOpen(open)
          if (!open) setSelectedProduct(null)
        }}
        selectedProduct={selectedProduct}
        onSuccess={handleSellSuccess}
        showCustomerPicker
        showSellingTypePicker
      />
    </>
  )
}
